import React from "react";
import { Link } from "react-router-dom";
import type { LinkProps } from "react-router-dom";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import type { LocationState } from "history";

import styles from "./IconButton.module.css";

export const variantOptions = [
	"primary",
	"primary-ai",
	"secondary",
	"tertiary",
	"destructive",
	"billing",
	"placeholder",
	"outlined",
] as const;

export const sizeOptions = ["large", "normal", "small"] as const;

export type FellowIconButtonProps = {
	size?: (typeof sizeOptions)[number];
	variant?: (typeof variantOptions)[number];
	disabled?: boolean;
	icon: IconProp;
	iconProps?: Omit<FontAwesomeIconProps, "icon">;
};

export type IconButtonProps = Omit<React.HTMLProps<HTMLButtonElement>, "ref" | "size"> &
	React.ButtonHTMLAttributes<HTMLButtonElement> &
	FellowIconButtonProps;

/**
 * This is our button. There are many like it, but this one is ours.
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
	(
		{
			className,
			size = "normal",
			variant = "secondary",
			icon,
			iconProps: { className: iconClassName, ...restIconProps } = {},
			type = "button",
			...rest
		},
		ref,
	) => {
		const classNames = cx(styles.iconButton, styles[`size-${size}`], styles[`variant-${variant}`], className, {
			[styles.disabled]: rest.disabled,
		});
		const iconClassNames = cx(styles.icon, iconClassName);

		return (
			<button ref={ref} type={type} {...rest} className={classNames}>
				<FontAwesomeIcon icon={icon} className={iconClassNames} {...restIconProps} />
			</button>
		);
	},
);

IconButton.displayName = "IconButton";

export type IconLinkButtonProps<LS extends LocationState> = LinkProps<LS> & FellowIconButtonProps;

/**
 * An icon button that can trigger navigation.
 */
export const IconLinkButton = React.forwardRef(function IconLinkButton<T extends LocationState>(
	{
		className,
		size = "normal",
		variant = "secondary",
		icon,
		iconProps: { className: iconClassName, ...restIconProps } = {},
		...rest
	}: IconLinkButtonProps<T>,
	ref: React.Ref<HTMLAnchorElement>,
) {
	const classNames = cx(styles.iconButton, styles[`size-${size}`], styles[`variant-${variant}`], className, {
		[styles.disabled]: rest.disabled,
	});
	const iconClassNames = cx(styles.icon, iconClassName);

	return (
		<Link<T> {...rest} ref={ref} className={classNames}>
			<FontAwesomeIcon icon={icon} className={iconClassNames} {...restIconProps} />
		</Link>
	);
});

IconLinkButton.displayName = "IconLinkButton";

export type AnchorIconButtonProps = Omit<React.HTMLProps<HTMLAnchorElement>, "ref" | "size"> &
	React.AnchorHTMLAttributes<HTMLAnchorElement> &
	FellowIconButtonProps;

/**
 * An anchor that looks like an icon button.
 */
export const IconAnchorButton = React.forwardRef<HTMLAnchorElement, AnchorIconButtonProps>(
	(
		{
			className,
			size = "normal",
			variant = "secondary",
			icon,
			iconProps: { className: iconClassName, ...restIconProps } = {},
			...rest
		},
		ref,
	) => {
		const classNames = cx(styles.iconButton, styles[`size-${size}`], styles[`variant-${variant}`], className, {
			[styles.disabled]: rest.disabled,
		});
		const iconClassNames = cx(styles.icon, iconClassName);

		return (
			<a {...rest} ref={ref} className={classNames}>
				<FontAwesomeIcon icon={icon} className={iconClassNames} {...restIconProps} />
			</a>
		);
	},
);

IconAnchorButton.displayName = "IconAnchorButton";

export default IconButton;
